import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Country context — provides the active country code (UK | USA) +
 * currency to every storefront component.
 *
 * Source of truth, in priority order:
 *   1. signed-in user's `country` field
 *   2. explicit URL query `?country=UK|USA`
 *   3. previously-saved `fg_country` in localStorage
 *   4. server-side default (UK for Phase-1)
 *
 * When (1), (2) or (3) yields a value we don't show the picker.
 * When none do, the picker pops on the next storefront page render.
 */
const CountryContext = createContext(null);
const LS_KEY = "fg_country";

export function CountryProvider({ children }) {
  const { user } = useAuth() || {};
  const [country, setCountryState] = useState(null);
  const [supported, setSupported] = useState([]);
  const [defaultCountry, setDefault] = useState("UK");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Load supported countries once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/v2/countries");
        if (cancelled) return;
        setSupported(data?.countries || []);
        setDefault(data?.default || "UK");
      } catch {
        // Phase-1 sane fallback when /v2/countries isn't live yet
        if (cancelled) return;
        setSupported([
          { code: "UK", label: "United Kingdom", currency: "GBP", default: true },
          { code: "USA", label: "United States", currency: "USD" },
        ]);
        setDefault("UK");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve the active country once we know who the user is + we have the catalog
  useEffect(() => {
    if (!supported.length) return;
    let resolved = null;

    // 1. signed-in user's profile
    if (user?.country) {
      resolved = user.country.toUpperCase();
    }
    // 2. URL ?country=
    if (!resolved && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      const q = (url.searchParams.get("country") || "").toUpperCase();
      if (q && supported.some((c) => c.code === q)) resolved = q;
    }
    // 3. localStorage
    if (!resolved && typeof window !== "undefined") {
      try {
        const saved = (localStorage.getItem(LS_KEY) || "").toUpperCase();
        if (saved && supported.some((c) => c.code === saved)) resolved = saved;
      } catch { /* ignore */ }
    }
    // 4. server default — but DON'T set it; trigger the picker instead
    if (resolved) {
      setCountryState(resolved);
      setPickerOpen(false);
    } else {
      setCountryState(defaultCountry);    // optimistic: render with default
      setPickerOpen(true);                // but ask the user
    }
    setHydrated(true);
  }, [user, supported, defaultCountry]);

  const setCountry = useCallback((code) => {
    const c = (code || "").toUpperCase();
    setCountryState(c);
    setPickerOpen(false);
    try { localStorage.setItem(LS_KEY, c); } catch { /* ignore */ }
    // Persist to logged-in user profile in the background (fire-and-forget).
    if (user && user.id) {
      api.patch("/auth/me", { country: c }).catch(() => { /* non-fatal */ });
    }
  }, [user]);

  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);

  const currency = supported.find((c) => c.code === country)?.currency || "GBP";

  return (
    <CountryContext.Provider
      value={{
        country: country || defaultCountry,
        currency,
        supported,
        defaultCountry,
        setCountry,
        pickerOpen,
        openPicker,
        closePicker,
        hydrated,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  return useContext(CountryContext);
}
